import { Component, OnInit, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { HeaderComponent } from './header/header.component';

import { User } from './shared/models/user';
import { AuthService } from './shared/services/auth.service';
import Utils from './shared/utilities/utils';

const IDS_TOKEN_KEY = 'i';
const STUDIO_LAUNCH_KEY = 'academy-opened-from-studio';
const TOKEN_INFO_KEY = 'pub-ref-id';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  imports: [HeaderComponent, RouterOutlet],
  standalone: true,
})
export class AppComponent implements OnInit {
  isAuthenticated = signal(false);
  showApplication = signal(false);
  isProcessingStudioLogin = signal(false);
  isViewingLoginPage = false;
  loggedInUser: User | null = null;
  isMicrosoft: boolean;

  constructor(
    private _router: Router,
    private _authService: AuthService
  ) {
    this.isMicrosoft = Utils.detectMicrosoft();
  }

  onLogin() {
    this._router.navigate(['login']);
  }

  onLogout() {
    const launchedFromStudio = this.wasLaunchedFromStudio();
    this._authService.logout();
    this.isAuthenticated.set(false);
    window.sessionStorage.removeItem(IDS_TOKEN_KEY);
    if (launchedFromStudio) {
      window.sessionStorage.removeItem(STUDIO_LAUNCH_KEY);
      window.open('', '_self');
      window.close();
    } else {
      this._router.navigate(['']);
    }
  }

  ngOnInit() {
    this._authService.errors$.subscribe(() => {
      this.isProcessingStudioLogin.set(false);
      this.showApplication.set(true);
    });

    this._authService.isLoggedIn$.subscribe(() => {
      const identityToken = this.getIdentityToken();
      this.isAuthenticated.set(this._authService.isAuthenticated());
      if (this.isAuthenticated()) {
        this.isProcessingStudioLogin.set(false);
        const redirect = this._authService.redirectUrl ? this._authService.redirectUrl : '/courses';
        this._router.navigate([redirect]);
        this.showApplication.set(true);
      } //if they are trying to login and have an identity token then just log them in
      else if (identityToken) {
        this.isProcessingStudioLogin.set(true);
        this.showApplication.set(true);
        this.loginFromStudio(identityToken);
      } else {
        this.isProcessingStudioLogin.set(false);
        // show the login page for unauthenticated users
        this.showApplication.set(true);
      }
    });

    this._authService.checkLoginStatus();
  }

  private loginFromStudio(idToken: string) {
    this._authService.loginFromStudio(idToken);
  }

  private getIdentityToken(): string | null {
    const fragmentToken = this.getIdentityTokenFromFragment();
    if (fragmentToken) {
      window.sessionStorage.setItem(IDS_TOKEN_KEY, fragmentToken);
      window.sessionStorage.setItem(STUDIO_LAUNCH_KEY, 'true');
      this.clearUrlFragment();
      return fragmentToken;
    }

    const sessionToken = window.sessionStorage.getItem(IDS_TOKEN_KEY);
    if (sessionToken) {
      return sessionToken;
    }

    const persistedTokenInfo = window.localStorage.getItem(TOKEN_INFO_KEY);
    if (!persistedTokenInfo) {
      return null;
    }

    try {
      const payload = JSON.parse(persistedTokenInfo) as { value?: string; expiry?: number | null };
      if (payload.expiry && Date.now() > payload.expiry) {
        window.localStorage.removeItem(TOKEN_INFO_KEY);
        return null;
      }

      if (!payload.value) {
        return null;
      }

      const authToken = JSON.parse(window.atob(payload.value)) as { idToken?: string | null };
      return authToken.idToken ?? null;
    } catch {
      return null;
    }
  }

  private getIdentityTokenFromFragment(): string | null {
    const hash = window.location.hash;
    if (!hash || hash.length <= 1) {
      return null;
    }

    const fragmentValue = hash.slice(1);
    if (!fragmentValue.includes('=')) {
      return decodeURIComponent(fragmentValue);
    }

    const params = new URLSearchParams(fragmentValue);
    const identityToken =
      params.get('identityToken') ?? params.get('token') ?? params.get('idToken');
    return identityToken ? decodeURIComponent(identityToken) : null;
  }

  private clearUrlFragment() {
    const urlWithoutFragment = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, '', urlWithoutFragment);
  }

  private wasLaunchedFromStudio(): boolean {
    return window.sessionStorage.getItem(STUDIO_LAUNCH_KEY) === 'true';
  }
}
